import {
  Accordion,
  AccordionContent,
  AccordionItem,
} from '@cohort/shared-frontend/components/Accordion';
import OAuthErrorModal from '@cohort/wallet/components/modals/oauth/OAuthErrorModal';
import Navbar from '@cohort/wallet/components/navigation/NavBar';
import TestModeBanner from '@cohort/wallet/components/testMode/TestModeBanner';
import useOauthError from '@cohort/wallet/hooks/oAuthError';
import {useAppStore} from '@cohort/wallet/hooks/stores/app';
import {useUserStore} from '@cohort/wallet/hooks/stores/user';
import {useMerchantContext} from '@cohort/wallet/hooks/useMerchantContext';
import useScreenSizeContext from '@cohort/wallet/hooks/useScreenSize';
import useSpaceContext from '@cohort/wallet/hooks/useSpaceContext';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import SpaceBurgerNavigation from '@cohort/wallet/layouts/space/SpaceBurgerNavigation';
import SpaceNavbarNavigation from '@cohort/wallet/layouts/space/SpaceNavbarNavigation';
import SpaceTabbar from '@cohort/wallet/layouts/space/SpaceTabbar';
import {setAmplitudeMetadata} from '@cohort/wallet/lib/Tracking';
import {List} from '@phosphor-icons/react';
import {AccordionTrigger} from '@radix-ui/react-accordion';
import {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {Outlet, useNavigate} from 'react-router-dom';
import {match} from 'ts-pattern';
import {shallow} from 'zustand/shallow';

const SpaceLayout = (): JSX.Element => {
  const {t} = useTranslation('layouts', {
    keyPrefix: 'space.spaceLayout',
  });
  const {setTransitioning} = useSpaceContext();
  const {setMerchantTheme, accentColor} = useThemeContext();
  const navigate = useNavigate();
  const {isMobile} = useScreenSizeContext();
  const {withNavbar, navigationType} = useAppStore(
    store => ({
      withNavbar: store.withNavbar,
      navigationType: store.navigationType,
    }),
    shallow
  );
  const user = useUserStore(store => store.user);
  const merchant = useMerchantContext();
  const [oauthError, setOauthError] = useOauthError();

  useEffect(() => {
    setAmplitudeMetadata(merchant);
  }, [merchant]);

  useEffect(() => {
    setMerchantTheme(merchant);
  }, [merchant, setMerchantTheme]);
  const navbarEnabled = withNavbar && user !== null;

  const navbar = match({
    navbarEnabled,
    navigationType,
    isMobile,
  })
    .with(
      {
        navbarEnabled: true,
      },
      () => (
        <Navbar
          handleBackButtonClick={() => {
            setTransitioning({active: true, enterFrom: 'right'});
            navigate(-1);
          }}
          logoFileKey={merchant.logoFileKey}
          appTitle={t('experienceSpaceTitle', {name: merchant.name})}
        >
          {isMobile && navigationType === 'burger' && <SpaceBurgerNavigation />}
          {!isMobile && <SpaceNavbarNavigation />}
        </Navbar>
      )
    )
    .with(
      {
        navbarEnabled: false,
        navigationType: 'burger',
        isMobile: true,
      },
      () => (
        <Accordion type="single" collapsible className="z-40 w-full px-4 py-2">
          <AccordionItem value="navbar" className="border-0">
            <AccordionTrigger className="ml-auto block">
              <List color={accentColor} size={24} />
            </AccordionTrigger>
            <AccordionContent className="p-0">
              <SpaceBurgerNavigation />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )
    )
    .otherwise(() => <div />);

  return (
    <div
      className="relative grid overflow-hidden [grid-template-rows:min-content_max-content_1fr_min-content]"
      style={{
        height: '100svh',
        maxHeight: '100lvh',
        gridTemplateAreas: `
          "testModeBanner"
          "header"
          "main"
          "tabbar"
        `,
      }}
    >
      <TestModeBanner />
      {navbar}
      <div className="h-full overflow-auto">
        <Outlet />
        {oauthError && (
          <OAuthErrorModal error={oauthError} onClose={() => setOauthError(undefined)} />
        )}
      </div>
      {isMobile && user && navigationType === 'tabbar' && <SpaceTabbar />}
    </div>
  );
};

export default SpaceLayout;
